import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, KeyRound, CheckCircle2, AlertCircle } from "lucide-react";

import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { PasswordInput } from "../components/ui/password-input";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      setError("This reset link is missing its token.");
      return;
    }
    if (pwd.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (pwd !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-md p-8" data-testid="reset-password-card">
        {done ? (
          <div className="text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-50 grid place-items-center mx-auto">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <h2 className="font-heading text-2xl font-semibold mt-4 text-slate-900" data-testid="reset-password-success">Password updated</h2>
            <p className="text-sm text-slate-500 mt-2">Sending you back to sign in…</p>
          </div>
        ) : (
          <form onSubmit={onSubmit} data-testid="reset-password-form">
            <p className="label-overline">Account recovery</p>
            <h2 className="font-heading text-2xl font-semibold mt-2 text-slate-900">Choose a new password</h2>
            <p className="text-sm text-slate-500 mt-1">
              Use 8 characters or more. Mix letters, numbers, and symbols.
            </p>
            <div className="mt-6 space-y-4">
              <div>
                <Label htmlFor="pwd" className="label-overline">New password</Label>
                <PasswordInput
                  id="pwd"
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  required
                  placeholder="At least 8 characters"
                  className="mt-2"
                  testid="reset-password-new"
                />
              </div>
              <div>
                <Label htmlFor="confirm" className="label-overline">Confirm new password</Label>
                <PasswordInput
                  id="confirm"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  placeholder="Repeat your password"
                  className="mt-2"
                  testid="reset-password-confirm"
                />
              </div>
              {error && (
                <div className="border border-red-200 bg-red-50 text-red-700 text-sm p-3 rounded-sm flex items-start gap-2" data-testid="reset-password-error">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
              <Button type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="reset-password-submit">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                Update password
              </Button>
              <Link to="/login" className="block text-center text-sm text-slate-500 hover:text-slate-700">Back to sign in</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
